import EditableText from "../ui/EditableText"

export interface ExperienceInput {
  period: string
  lines: string[]
}

const defaultExperiences: ExperienceInput[] = [
  {
    period: "Since 10/2015",
    lines: ["ip assets licensing GmbH, Zug, Switzerland", "CEO, Owner"],
  },
  {
    period: "Since 07/2013",
    lines: ["DANTZ IP & LAW", "European Patent Attorney, Owner"],
  },
  {
    period: "10/2010 to 09/2015",
    lines: ["ipal GmbH, Berlin", "CEO"],
  },
  {
    period: "05/2009 to 09/2010",
    lines: ["REV Renewable Energy Ventures Inc., Zug, Switzerland", "CEO"],
  },
  {
    period: "04/2008 to 04/2009",
    lines: [
      "City Solar AG, Bad Kreuznach",
      "Vice President, Intellectual Property Management",
    ],
  },
  {
    period: "01/2006 to 03/2008",
    lines: [
      "Faurecia Autositze GmbH, Stadthagen",
      "Head of Department, Intellectual Property Germany",
    ],
  },
  {
    period: "03/2004 to 12/2005",
    lines: ["Volkswagen AG, Wolfsburg", "IP Manager, Electronic"],
  },
  {
    period: "01/2001 to 03/2004",
    lines: ["Wacker Siltronic AG, Burghausen", "Technology / R&D Management"],
  },
  {
    period: "03/1997 to 12/2000",
    lines: ["Hahn-Meitner-Institut, Berlin", "PhD Student, Postdoc"],
  },
]

interface CurriculumVitaeSectionProps {
  name?: string
  credentials?: string
  subheading?: string
  experienceLabel?: string
  experiences?: ExperienceInput[]
  rolesLabel?: string
  rolesText?: string
  editable?: boolean
  onSaveName?: (value: string) => void
  onSaveCredentials?: (value: string) => void
  onSaveSubheading?: (value: string) => void
  onSaveExperienceLabel?: (value: string) => void
  onSaveExperiencePeriod?: (index: number, value: string) => void
  onSaveExperienceLine?: (index: number, lineIndex: number, value: string) => void
  onSaveRolesLabel?: (value: string) => void
  onSaveRolesText?: (value: string) => void
}

export default function CurriculumVitaeSection({
  name = "DR. DIRK DANTZ",
  credentials = "Dr. rer. nat. · Dipl.-Min. · European Patent Attorney",
  subheading = "Curriculum vitae",
  experienceLabel = "Professional experience",
  experiences = defaultExperiences,
  rolesLabel = "Additional roles",
  rolesText = "Dr. Dantz is also an expert for the IBB ProFIT funding programme on behalf of the State of Berlin and serves as an advisory board member for the Berlin Brandenburg Innovation Award.",
  editable = false,
  onSaveName,
  onSaveCredentials,
  onSaveSubheading,
  onSaveExperienceLabel,
  onSaveExperiencePeriod,
  onSaveExperienceLine,
  onSaveRolesLabel,
  onSaveRolesText,
}: CurriculumVitaeSectionProps) {
  return (
    <section className="bg-white px-4 pb-16 pt-16 md:px-8">
      <div className="mx-auto max-w-6xl text-dantzBlue">
        <div className="mx-auto w-11/12 border-t border-dantzBlue" />

        <EditableText
          as="h2"
          value={name}
          editMode={editable}
          onSave={(value) => onSaveName?.(value)}
          className="mt-12 block text-center text-[24px] font-normal tracking-wide"
        />
        <EditableText
          as="p"
          value={credentials}
          editMode={editable}
          onSave={(value) => onSaveCredentials?.(value)}
          className="mt-3 block text-center text-sm leading-relaxed text-gray-600"
        />

        <EditableText
          as="h3"
          value={subheading}
          editMode={editable}
          onSave={(value) => onSaveSubheading?.(value)}
          className="mt-14 block text-[20px] font-normal tracking-wide"
        />

        <EditableText
          as="p"
          value={experienceLabel}
          editMode={editable}
          onSave={(value) => onSaveExperienceLabel?.(value)}
          className="mt-8 block font-semibold"
        />

        <div className="mt-6 flex flex-col gap-4 text-sm leading-relaxed">
          {experiences.map((experience, index) => (
            <div
              key={index}
              className="grid grid-cols-1 gap-x-8 md:grid-cols-[190px_1fr]"
            >
              <EditableText
                as="span"
                value={experience.period}
                editMode={editable}
                onSave={(value) => onSaveExperiencePeriod?.(index, value)}
              />
              <div>
                {experience.lines.map((line, lineIndex) => (
                  <EditableText
                    key={lineIndex}
                    as="span"
                    value={line}
                    editMode={editable}
                    onSave={(value) => onSaveExperienceLine?.(index, lineIndex, value)}
                    className="block"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <EditableText
          as="p"
          value={rolesLabel}
          editMode={editable}
          onSave={(value) => onSaveRolesLabel?.(value)}
          className="mt-12 block font-semibold"
        />
        <EditableText
          as="p"
          value={rolesText}
          editMode={editable}
          multiline
          onSave={(value) => onSaveRolesText?.(value)}
          className="mt-3 block max-w-3xl text-sm leading-relaxed text-gray-600"
        />

        <div className="relative mt-16">
          <div className="h-px w-full bg-dantzBlue" />
          <div className="absolute bottom-0 h-4 w-px bg-dantzBlue" style={{ left: "62%" }} />
          <div className="absolute bottom-0 right-0 h-4 w-px bg-dantzBlue" />
        </div>
      </div>
    </section>
  )
}
